import { Link } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon, Building2, Sun, BatteryCharging, Plug, Zap, Wrench, Thermometer, ShieldCheck, Award, MapPin } from "lucide-react";
import { CONTACT, SERVICES, MELBOURNE_METRO, REGIONAL_VIC } from "../data/site";

const ICONS = { Home: HomeIcon, Building2, Sun, BatteryCharging, Plug, Zap, Wrench, Thermometer };

const TESTIMONIALS = [
  "★★★★★  Turned up on time, priced fair, cleaned up after — Andrew is a proper sparky. — Sarah, Yarraville",
  "★★★★★  Our solar+battery install has smashed our power bill. — Dean, Point Cook",
  "★★★★★  Switchboard upgrade done in a day, no fuss. — Priya, Brunswick",
  "★★★★★  Booked an EV charger, sorted within the week. — Josh, Werribee",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-center border-b-2 border-black overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1595434971780-79d5c20c5090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtZWxib3VybmUlMjBza3lsaW5lfGVufDB8fHx8MTc4MjkwMTQ1OXww&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.9) 100%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <p className="overline text-zinc-400 rise-1" data-testid="hero-overline">
            <span className="text-[#E62117] mr-3">◆</span> Melbourne · Regional Victoria · Since Day One
          </p>
          <h1
            data-testid="hero-title"
            className="font-display text-white text-6xl sm:text-7xl lg:text-9xl uppercase leading-[0.88] mt-6 rise-2"
          >
            Powering<br />
            Melbourne's<br />
            <span className="text-[#E62117]">Future.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl rise-3">
            Electrical, solar & battery installations by licensed Melbourne trades.
            Straight-talking quotes, tidy workmanship, and warranties that mean something.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 rise-4">
            <Link to="/contact" data-testid="hero-cta-quote" className="btn-primary">
              Get a Free Quote <ArrowUpRight className="ml-2" size={18} />
            </Link>
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="hero-cta-call" className="btn-ghost-dark">
              Call {CONTACT.phone}
            </a>
          </div>

          {/* Stat bar */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-white/20 pt-8">
            {[
              { k: "Licensed", v: "REC 32035" },
              { k: "Insured", v: "$20M PL" },
              { k: "Rated", v: "5.0 ★★★★★" },
              { k: "Australian", v: "100% Owned" },
            ].map((s) => (
              <div key={s.k} className="text-white border-l border-white/20 first:border-l-0 px-4">
                <p className="overline text-zinc-500">{s.k}</p>
                <p className="font-display text-2xl md:text-3xl mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section
        data-testid="home-services-section"
        className="py-24 md:py-32 bg-white border-b-2 border-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
            <div>
              <p className="overline"><span className="text-[#E62117] mr-2">01</span>What we do</p>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase mt-4 leading-[0.95]">
                Every wire,<br />every watt.
              </h2>
            </div>
            <Link to="/services" data-testid="home-services-all" className="btn-secondary self-start md:self-end">
              All Services <ArrowUpRight className="ml-2" size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Zap;
              // Bento sizing
              const span = i === 0 ? "md:col-span-3" : i === 1 ? "md:col-span-3" : "md:col-span-2";
              return (
                <Link
                  key={s.slug}
                  to="/services"
                  data-testid={`service-card-${s.slug}`}
                  className={`card-industrial ${span} group flex flex-col justify-between min-h-[220px]`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-14 h-14 border-2 border-black flex items-center justify-center bg-[#E62117] text-white">
                      <Icon size={26} />
                    </div>
                    <span className="font-mono-brand text-xs text-zinc-500">0{i + 1}</span>
                  </div>
                  <div className="mt-6">
                    <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{s.short}</p>
                  </div>
                  <div className="mt-6 flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
                    Learn more <ArrowUpRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section
        data-testid="home-why-section"
        className="py-24 md:py-32 bg-zinc-950 text-white border-b-2 border-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline text-zinc-500"><span className="text-[#E62117] mr-2">02</span>Why Andstel</p>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase mt-4 leading-[0.95] max-w-4xl">
            We show up. We do it right. We back it.
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "Licensed & Insured", body: "Fully licensed Victorian electricians with $20M public liability insurance. Certificates of Electrical Safety on every job." },
              { icon: Award, title: "Manufacturer-Certified", body: "Approved installers for Tesla, Sungrow, SolarEdge and leading EV charger brands. Warranties honoured, not just quoted." },
              { icon: MapPin, title: "Local & Reachable", body: "Based in Melbourne's west. When you call, you speak to Andrew — the person actually doing the work." },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`why-card-${i}`} className="card-dark">
                  <Icon size={36} className="text-[#E62117]" />
                  <h3 className="font-display text-3xl uppercase mt-6">{f.title}</h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marquee testimonials */}
      <section data-testid="home-testimonials" className="py-16 bg-[#E62117] text-white border-b-2 border-black overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap gap-16 font-mono-brand text-lg md:text-2xl font-semibold uppercase tracking-wide">
          {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
            <span key={i} className="flex items-center gap-16">
              {t}
              <span>◆</span>
            </span>
          ))}
        </div>
      </section>

      {/* Areas snippet */}
      <section data-testid="home-areas-section" className="py-24 md:py-32 bg-white border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline"><span className="text-[#E62117] mr-2">03</span>Where we work</p>
            <h2 className="font-display text-5xl md:text-6xl uppercase mt-4 leading-[0.95]">
              All over Melbourne. Deep into VIC.
            </h2>
            <p className="mt-6 text-zinc-700 leading-relaxed max-w-md">
              We cover every metro suburb and most regional towns. Not sure if we make it to you?
              Ask — we probably do.
            </p>
            <Link to="/areas" data-testid="home-areas-cta" className="btn-primary mt-8">
              See All Areas <ArrowUpRight className="ml-2" size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-6">
            <div className="card-industrial">
              <p className="overline">Metro</p>
              <p className="font-display text-4xl md:text-5xl mt-2">{MELBOURNE_METRO.length}+</p>
              <p className="mt-3 text-sm text-zinc-600">Suburbs across Melbourne — north, south, east and west.</p>
            </div>
            <div className="card-industrial">
              <p className="overline">Regional</p>
              <p className="font-display text-4xl md:text-5xl mt-2">{REGIONAL_VIC.length}+</p>
              <p className="mt-3 text-sm text-zinc-600">Regional Victorian towns and rural properties covered.</p>
            </div>
            <div className="card-industrial col-span-2">
              <p className="overline">Popular</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {MELBOURNE_METRO.slice(0, 12).map((sub) => (
                  <span key={sub} className="border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    {sub}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="home-cta-section" className="py-24 md:py-32 bg-[#E62117] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
            Ready when<br />you are.
          </h2>
          <div>
            <p className="text-lg md:text-xl leading-relaxed max-w-xl">
              Book a free on-site inspection or quote. We'll turn up when we say we will, listen properly,
              and give you a fixed price in writing.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="cta-book" className="bg-black text-white border-2 border-black font-bold uppercase tracking-wide px-8 py-4 shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,1)] transition-all">
                Book Now
              </Link>
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="cta-call-final" className="border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#E62117] transition-colors">
                Call {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
